import React, { useState } from "react";
import { Button, Card, DataTable, ExpandedCard, Table, Tooltip, TooltipIcon, } from "@sscs/ui";
import { capitalizeFirstLetter } from "@sscs/utils";
import { SeverityCountChiclet } from "../../../../ui";
import SplxLogo from "../../../assets/images/icons/splx-logo-black.svg";
import { mapScoreToSeverity, sumProbeAttacks } from "../utils";
import styles from "./mlModel.module.scss";
const PROBE_COLUMNS = [
    { id: "category", label: "Category", width: "40%" },
    { id: "failed", label: "# Attacks failed", width: "20%" },
    { id: "succeed", label: "# Attacks succeed", width: "20%" },
    { id: "score", label: "Score", width: "20%" },
];
const ScoreBar = ({ score, className, }) => {
    return (React.createElement("div", { className: styles["score-bar"] },
        React.createElement("div", { className: `${styles["score-display"]} ${className}`, style: { width: `${score}%` } })));
};
const AISecurityCardTitle = ({ modelCard }) => {
    const { totalAttacks, successfulAttacks } = sumProbeAttacks(modelCard.testing.splx_risks);
    return (React.createElement("div", { className: styles["ai-security-header"] },
        React.createElement("div", null,
            React.createElement("div", { className: styles["ai-security-title"] },
                React.createElement("span", null, "AI security"),
                " ",
                modelCard.testing !== undefined && modelCard.testing.present && (React.createElement(React.Fragment, null,
                    React.createElement("span", null,
                        "|\u00A0",
                        capitalizeFirstLetter(mapScoreToSeverity(modelCard.testing.splx_score)),
                        " ",
                        "Risk - Score: ",
                        modelCard.testing.splx_score.toFixed(2),
                        "/100"),
                    React.createElement(TooltipIcon, { title: React.createElement(React.Fragment, null,
                            React.createElement("div", null, "Overall Score summarizes model performance. It shows the number of simulated attacks and successful attacks across different probe categories. It ranges 0-100, where lower numbers mean higher risk:"),
                            React.createElement("ul", null,
                                React.createElement("li", null, "Critical risk (<30): Immediate action required"),
                                React.createElement("li", null, "High risk (30 - 59.9): Prioritize mitigation"),
                                React.createElement("li", null, "Medium risk (60 - 79.9): Monitor closely and address issues"),
                                React.createElement("li", null, "Low risk (>=80): Routine monitoring and improvements"))) })))),
            modelCard.testing !== undefined && modelCard.testing.present && (React.createElement("div", { className: styles.subtitle },
                "Simulated attacks: ",
                totalAttacks,
                "\u00A0|\u00A0Successful attacks: ",
                successfulAttacks))),
        React.createElement(Tooltip, { trigger: "graphic", title: React.createElement("div", { className: styles["splx-logo-tooltip"] },
                "Testing ",
                modelCard.testing.present ? "was" : "is",
                " performed in partnership with ",
                React.createElement("a", { href: "https://splx.ai/" }, "Splx.ai")) },
            React.createElement("div", { className: styles["splx-logo-container"] },
                React.createElement(SplxLogo, null)))));
};
const AISecurityCategoryCard = ({ modelCard, category, rowIndex, isFocused, onFocus, }) => {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const riskScore = modelCard.testing.splx_risks[category].risk_score;
    const probeNames = Object.keys(modelCard.testing.splx_risks[category].test_probes);
    const { categorySimulatedAttacks, categorySuccessfulAttacks } = Object.values(modelCard.testing.splx_risks[category].test_probes).reduce((acc, probe) => {
        acc.categorySimulatedAttacks += probe.test_count;
        acc.categorySuccessfulAttacks += probe.test_fails;
        return acc;
    }, { categorySimulatedAttacks: 0, categorySuccessfulAttacks: 0 });
    const severity = mapScoreToSeverity(riskScore);
    return (React.createElement(ExpandedCard.Card, { id: `${rowIndex}-${category}`, isExpanded: isExpanded, onChange: (expanded) => setIsExpanded(expanded), isFocused: isFocused, onFocus: onFocus, disableFullscreen: true },
        React.createElement(ExpandedCard.Title, null, category),
        React.createElement(ExpandedCard.Summary, null,
            React.createElement("div", { className: styles["splx-category-summary"] },
                React.createElement("div", { className: styles["splx-category-probes"] },
                    React.createElement("span", null,
                        "Attacks performed:",
                        React.createElement("br", null),
                        probeNames.join(", ")),
                    React.createElement(Button, { variant: "text", size: "small", onClick: () => setIsExpanded(!isExpanded), className: styles["show-more"] }, !isExpanded ? "View detailed results" : "Show less")),
                React.createElement("div", { className: styles["splx-category-metrics"] },
                    React.createElement("div", { className: styles["performance-score"] },
                        React.createElement("div", { className: `${styles["severity"]} ${styles[`severity-${severity}`]}` }, severity.toUpperCase().slice(0, 1)),
                        "Performance score: ",
                        riskScore.toFixed(2),
                        "/100"),
                    React.createElement(ScoreBar, { score: riskScore, className: styles[`bar-${severity}`] }),
                    React.createElement("div", { className: styles["splx-category-attack-counts"] },
                        "Successful attacks: ",
                        categorySuccessfulAttacks,
                        " out of",
                        " ",
                        categorySimulatedAttacks,
                        " attempted")))),
        React.createElement(ExpandedCard.Details, null,
            React.createElement(DataTable, { columns: PROBE_COLUMNS, tableClassName: styles["probe-table"] }, probeNames.map((probe) => {
                const { test_count: probeAttacksTotal, test_fails: probeSuccessfulAttacks, test_score: probeScore, } = modelCard.testing.splx_risks[category].test_probes[probe];
                return (React.createElement(Table.Row, { key: probe },
                    React.createElement(Table.Cell, null, probe),
                    React.createElement(Table.Cell, null, probeAttacksTotal - probeSuccessfulAttacks),
                    React.createElement(Table.Cell, null, probeSuccessfulAttacks),
                    React.createElement(Table.Cell, null,
                        React.createElement(SeverityCountChiclet, { severity: mapScoreToSeverity(probeScore), count: probeScore, fractionDigits: 2 }))));
            })))));
};
const AISecurityCategoryCards = ({ modelCard, rowIndex, }) => {
    const [focusedCategory, setFocusedCategory] = useState("");
    return (React.createElement("div", { className: styles["categories-wrapper"] }, Object.keys(modelCard.testing.splx_risks).map((categoryName) => (React.createElement(AISecurityCategoryCard, { key: categoryName, modelCard: modelCard, category: categoryName, rowIndex: rowIndex, isFocused: focusedCategory === categoryName, onFocus: () => setFocusedCategory(categoryName) })))));
};
const AISecurityCard = ({ modelCard, rowIndex, }) => {
    return (React.createElement(Card.Card, null,
        React.createElement(Card.Title, null,
            React.createElement(AISecurityCardTitle, { modelCard: modelCard })),
        React.createElement(Card.Content, null,
            (modelCard.testing === undefined || !modelCard.testing.present) && (React.createElement("span", null,
                "No automated LLM red team testing data available for this model.",
                " ",
                React.createElement("a", { className: styles["support-link"], href: "mailto:support@reversinglabs.com" }, "Contact ReversingLabs support"),
                " ",
                "to request testing.")),
            React.createElement(AISecurityCategoryCards, { modelCard: modelCard, rowIndex: rowIndex }))));
};
export default AISecurityCard;
//# sourceMappingURL=AISecurityCard.js.map